import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormControl } from "react-bootstrap";
import "./UnitInput.scss";

const UnitInput = ({
	id,
	value,
	onChange,
	unitLabel,
	placeholder = "",
	type = "text",
	isInvalid = false,
	disabled = false,
	as = "input",
	customSelect = false,
	className = "",
	controlClassName = "",
	style,
	selectAllOnFocus = false,
	onFocus,
	children,
	...rest
}) => {
	const inputRef = useRef(null);
	const customSelectRef = useRef(null);
	const customSelectContainerRef = useRef(null);
	const [isCustomSelectOpen, setIsCustomSelectOpen] = useState(false);
	const isCustomSelect = as === "select" && customSelect;

	const customOptions = useMemo(
		() =>
			React.Children.toArray(children)
				.filter((child) => React.isValidElement(child) && child.type === "option")
				.map((child) => ({
					value: child.props.value ?? "",
					label: child.props.children,
					disabled: Boolean(child.props.disabled),
				})),
		[children]
	);

	const selectedOption = useMemo(
		() => customOptions.find((option) => String(option.value) === String(value)),
		[customOptions, value]
	);

	useEffect(() => {
		if (!isCustomSelectOpen) return undefined;

		const handleOutsideClick = (event) => {
			if (!customSelectContainerRef.current?.contains(event.target)) {
				setIsCustomSelectOpen(false);
			}
		};

		const handleEscape = (event) => {
			if (event.key === "Escape") {
				setIsCustomSelectOpen(false);
			}
		};

		document.addEventListener("mousedown", handleOutsideClick);
		document.addEventListener("keydown", handleEscape);

		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isCustomSelectOpen]);

	const focusInput = (event) => {
		event.preventDefault();
		if (isCustomSelect && !disabled) {
			customSelectRef.current?.focus();
			setIsCustomSelectOpen(true);
			return;
		}
		if (inputRef.current && !disabled) {
			inputRef.current.focus();
		}
	};

	const handleFocus = (event) => {
		if (selectAllOnFocus && event.target.select) {
			event.target.select();
		}
		if (onFocus) onFocus(event);
	};

	const handleCustomOptionSelect = (optionValue) => {
		setIsCustomSelectOpen(false);
		if (!onChange) return;

		onChange({
			target: {
				id,
				value: optionValue,
			},
			currentTarget: {
				id,
				value: optionValue,
			},
		});
	};

	return (
		<div
			className={`unit-input ${isCustomSelect ? "unit-input--custom-select" : ""} ${
				isInvalid ? "is-invalid" : ""
			} ${disabled ? "is-disabled" : ""} ${className}`}
			style={style}
		>
			{isCustomSelect ? (
				<div className="unit-input__control-wrap" ref={customSelectContainerRef}>
					<button
						type="button"
						ref={customSelectRef}
						id={id}
						disabled={disabled}
						className={`unit-input__control unit-input__control--select ${controlClassName} ${
							isCustomSelectOpen ? "is-open" : ""
						}`}
						onClick={() => setIsCustomSelectOpen(true)}
						onFocus={() => setIsCustomSelectOpen(true)}
					>
						<span className="unit-input__control-value">
							{selectedOption ? selectedOption.label : placeholder}
						</span>
						<span className="unit-input__control-arrow" aria-hidden="true">
							&#9662;
						</span>
					</button>

					{isCustomSelectOpen && (
						<div className="unit-input__select-menu">
							{customOptions.map((option) => {
								const isSelected = String(option.value) === String(value);
								return (
									<button
										type="button"
										key={`${id}-${option.value}`}
										className={`unit-input__select-option ${
											isSelected ? "is-selected" : ""
										}`}
										disabled={option.disabled}
										onMouseDown={(event) => event.preventDefault()}
										onClick={() => handleCustomOptionSelect(option.value)}
									>
										{option.label}
									</button>
								);
							})}
						</div>
					)}
				</div>
			) : (
				<FormControl
					ref={inputRef}
					as={as}
					id={id}
					value={value}
					onChange={onChange}
					onFocus={handleFocus}
					type={type}
					placeholder={placeholder}
					disabled={disabled}
					isInvalid={isInvalid}
					className={`unit-input__control ${controlClassName}`}
					{...rest}
				>
					{children}
				</FormControl>
			)}

			<div
				className="unit-input__separator"
				onMouseDown={focusInput}
				aria-hidden="true"
			/>

			<button
				type="button"
				className="unit-input__unit"
				onMouseDown={focusInput}
				disabled={disabled}
				tabIndex={-1}
			>
				{unitLabel}
			</button>
		</div>
	);
};

export default UnitInput;
